// FilterPanel.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Grid,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Box,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { getActiveDemandErrorMessages, getUniqueErrorMessagesFromData } from "../utils/demandErrorFlags";
import { getCurrentUser } from "../services/auth";
import preferenceApi from "../services/preferences";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/* ---------- Helpers ---------- */
const normalize = (s = "") =>
  String(s).toLowerCase().replace(/[^a-z0-9]/g, "").trim();

const humanizeKey = (key = "") =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function matchKeyForLabel(label, availableKeys) {
  if (!label || !availableKeys || availableKeys.length === 0) return null;
  const nLabel = normalize(label);

  const exact = availableKeys.find(k => k.toLowerCase() === label.toLowerCase());
  if (exact) return exact;

  const normMatch = availableKeys.find(k => normalize(k) === nLabel);
  if (normMatch) return normMatch;

  const humanMatch = availableKeys.find(k => normalize(humanizeKey(k)) === nLabel);
  if (humanMatch) return humanMatch;

  const partial = availableKeys.find(k =>
    normalize(humanizeKey(k)).includes(nLabel) || normalize(k).includes(nLabel)
  );
  if (partial) return partial;

  const camelGuess = label
    .split(/\s+/)
    .map((p, i) => (i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1)))
    .join("");
  const camelMatch = availableKeys.find(k => k === camelGuess);
  if (camelMatch) return camelMatch;

  return null;
}

/* ---------- FilterPanel component ---------- */
/**
 * Props:
 * - initialData: array of objects (original dataset)
 * - setData: function(filteredData) -> parent will update its displayed data
 * - template: array of labels e.g. ["Market","Skill Tower"]
 */
export default function FilterPanel({ initialData = [], setData, template = [], onClearAllRef, onSaveFilterRef, onActiveFiltersRef, compact = false }) {
  // derive available keys from initialData
  const availableKeys = useMemo(() => {
    if (!Array.isArray(initialData) || initialData.length === 0) return [];
    const set = new Set();
    initialData.forEach(row => Object.keys(row || {}).forEach(k => set.add(k)));
    return Array.from(set);
  }, [initialData]);

  // map template entries (string or {field,headerName,customSortOrder}) to keys using heuristics
  const filtersMeta = useMemo(() => {
    return (template || [])
      .map((item) => {
        if (item && typeof item === 'object' && item.field) {
          const keyCandidate = item.field;
          const key = availableKeys.includes(keyCandidate)
            ? keyCandidate
            : matchKeyForLabel(item.field, availableKeys);
          const label = item.headerName || item.field;
          const customSortOrder = item.customSortOrder || "";
          return key ? { label, key, customSortOrder } : null;
        }
        const label = item;
        const key = matchKeyForLabel(label, availableKeys);
        return key ? { label, key, customSortOrder: "" } : null;
      })
      .filter(Boolean);
  }, [template, availableKeys]);

  // compute distinct options for each matched key
  const BLANK_OPTION = "(Blank)";
  const optionsByKey = useMemo(() => {
    const out = {};
    filtersMeta.forEach(({ key, customSortOrder }) => {
      const set = new Set();
      let hasBlank = false;
      initialData.forEach(row => {
        const val = row[key];
        if (Array.isArray(val)) {
          val.forEach(v => { if (v !== undefined && v !== null && String(v).trim() !== "") set.add(String(v)); else hasBlank = true; });
        } else if (val === undefined || val === null || String(val).trim() === "") {
          hasBlank = true;
        } else {
          set.add(String(val));
        }
      });
      
      // Apply custom sort order if provided
      let sorted;
      if (customSortOrder && customSortOrder.trim()) {
        // Parse the comma-separated priority values
        const priorityValues = customSortOrder.split(',').map(v => v.trim()).filter(Boolean);
        const prioritySet = new Set(priorityValues.map(v => v.toLowerCase()));
        
        // Separate into priority items and remaining items
        const priorityItems = [];
        const remainingItems = [];
        
        Array.from(set).forEach(item => {
          const itemLower = item.toLowerCase();
          if (prioritySet.has(itemLower)) {
            priorityItems.push(item);
          } else {
            remainingItems.push(item);
          }
        });
        
        // Sort priority items according to their order in customSortOrder
        priorityItems.sort((a, b) => {
          const aIdx = priorityValues.findIndex(p => p.toLowerCase() === a.toLowerCase());
          const bIdx = priorityValues.findIndex(p => p.toLowerCase() === b.toLowerCase());
          return aIdx - bIdx;
        });
        
        // Sort remaining items alphabetically
        remainingItems.sort((a, b) => a.localeCompare(b));
        
        // Combine: priority items first, then remaining items
        sorted = [...priorityItems, ...remainingItems];
      } else {
        // Default alphabetical sort
        sorted = Array.from(set).sort((a, b) => a.localeCompare(b));
      }
      
      if (hasBlank) sorted.push(BLANK_OPTION);
      out[key] = sorted;
    });
    return out;
  }, [filtersMeta, initialData]);

  const [filters, setFilters] = useState({});
  const [selectedComplianceFlags, setSelectedComplianceFlags] = useState([]);
  const [savedPreference, setSavedPreference] = useState(null);
  const [preferenceReady, setPreferenceReady] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const complianceFlagOptions = useMemo(() => getUniqueErrorMessagesFromData(initialData), [initialData]);

  useEffect(() => {
    let mounted = true;
    preferenceApi.get("/demand_filters")
      .then(({ data }) => { if (mounted) setSavedPreference(data?.data || null); })
      .catch(() => { if (mounted) setSavedPreference(null); })
      .finally(() => { if (mounted) setPreferenceReady(true); });
    return () => { mounted = false; };
  }, []);

  // Apply one deterministic default after both the data and the user preference are ready.
  // A stored preference wins over the EDL/PDL name match.
  useEffect(() => {
    if (!preferenceReady || defaultsApplied || !filtersMeta.length) return;
    const next = {};
    const currentName = normalize(getCurrentUser()?.name || "");
    filtersMeta.forEach(({ key, label }) => {
      const options = optionsByKey[key] || [];
      if (savedPreference) {
        const savedValues = savedPreference[key];
        next[key] = Array.isArray(savedValues) ? savedValues.filter((value) => options.includes(value)) : [];
        return;
      }
      const field = normalize(label || key);
      if ((field === "edlname" || field === "pdlname") && currentName) {
        const matchingName = options.find((option) => normalize(option) === currentName);
        next[key] = matchingName ? [matchingName] : [];
      } else if (field === "geography") {
        const india = options.find((option) => normalize(option) === "india");
        next[key] = india ? [india] : [];
      } else if (field === "solinestatus") {
        const open = options.find((option) => normalize(option) === "open");
        next[key] = open ? [open] : [];
      } else {
        next[key] = [];
      }
    });
    setFilters(next);
    setSelectedComplianceFlags(Array.isArray(savedPreference?.__complianceFlags) ? savedPreference.__complianceFlags : []);
    setDefaultsApplied(true);
  }, [preferenceReady, defaultsApplied, filtersMeta, optionsByKey, savedPreference]);

  // apply filters and call setData
  useEffect(() => {
    if (typeof setData !== "function") return;
    if (!initialData || initialData.length === 0) return;

    const filtered = initialData.filter(row => {
      for (const { key } of filtersMeta) {
        const selected = filters[key] || [];
        if (!selected || selected.length === 0) continue;
        // When all available options are selected, treat as no filter
        const allOptions = optionsByKey[key] || [];
        if (selected.length >= allOptions.length) continue;
        const val = row[key];
        const isBlank = val === undefined || val === null || String(val).trim() === "";
        if (isBlank) {
          if (!selected.includes(BLANK_OPTION)) return false;
          continue;
        }
        if (Array.isArray(val)) {
          const has = val.some(v => selected.includes(String(v)));
          if (!has) return false;
        } else {
          if (!selected.includes(String(val))) return false;
        }
      }

      if (selectedComplianceFlags.length > 0) {
        if (selectedComplianceFlags.length < complianceFlagOptions.length) {
          const messages = getActiveDemandErrorMessages(row?.error_flag);
          const hasComplianceMatch = selectedComplianceFlags.some((message) => messages.includes(message));
          if (!hasComplianceMatch) return false;
        }
      }

      return true;
    });
    setData(filtered);
  }, [filters, initialData, filtersMeta, setData, optionsByKey, selectedComplianceFlags, complianceFlagOptions]);

  const handleChange = (key, newValue) => {
    setFilters(prev => ({ ...prev, [key]: newValue }));
  };

  const clearAll = useCallback(() => {
    const cleared = {};
    filtersMeta.forEach(({ key }) => (cleared[key] = []));
    setFilters(cleared);
    setSelectedComplianceFlags([]);
  }, [filtersMeta]);

  const saveFilters = useCallback(async () => {
    const data = { ...filters, __complianceFlags: selectedComplianceFlags };
    await preferenceApi.put("/demand_filters", { data });
    setSavedPreference(data);
    return true;
  }, [filters, selectedComplianceFlags]);

  // Expose clearAll to parent
  useEffect(() => {
    if (onClearAllRef) {
      onClearAllRef.current = clearAll;
    }
  }, [onClearAllRef, clearAll]);

  // Expose saveFilters to parent
  useEffect(() => {
    if (onSaveFilterRef) {
      onSaveFilterRef.current = saveFilters;
    }
  }, [onSaveFilterRef, saveFilters]);

  // Expose active filters state to parent (for Show Filters modal)
  useEffect(() => {
    if (onActiveFiltersRef) {
      onActiveFiltersRef.current = { filters, filtersMeta, complianceFlags: selectedComplianceFlags, complianceFlagOptions };
    }
  }, [onActiveFiltersRef, filters, filtersMeta, selectedComplianceFlags, complianceFlagOptions]);

  return (
    <Box sx={{ p: compact ? 0.5 : 1 }}>
      <Grid container spacing={compact ? 0.35 : 1}>
        {filtersMeta.map(({ label, key }) => {
          const options = optionsByKey[key] || [];
          const selected = filters[key] || [];
          const allSelected = options.length > 0 && selected.length === options.length;
          return (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={key}>
            <Autocomplete
              multiple
              limitTags={1}
              size="small" 
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  minHeight: 34,
                  maxHeight: 34,
                  overflow: 'hidden',
                  flexWrap: 'nowrap',
                  paddingTop: 0,
                  paddingBottom: 0,
                },
                '& .MuiAutocomplete-input': {
                  fontSize: compact ? '0.78rem' : '0.875rem',
                  minWidth: 0,
                },
                '& .MuiAutocomplete-tag': {
                  maxWidth: '100%',
                  margin: '1px 2px 1px 0',
                },
                '& .MuiChip-root': {
                  maxWidth: '100%',
                },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiAutocomplete-endAdornment': {
                  top: '50%',
                  transform: 'translateY(-50%)',
                },
              }}
              PaperProps={{ sx: { maxHeight: 200 } }} 

              disableCloseOnSelect
              options={options}
              value={selected}
              onChange={(e, newValue, reason, detail) => {
                // If user clicked the "__select_all__" virtual option via renderOption
                // we handle it here — but MUI won't include it in newValue because
                // it's intercepted below. So we use a custom flag on the event instead.
                handleChange(key, newValue);
              }}
              getOptionLabel={(opt) => opt}
              renderOption={(props, option, { selected: isSelected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={isSelected}
                  />
                  {option}
                </li>
              )}
              ListboxComponent={React.forwardRef(function ListboxWithSelectAll(listboxProps, ref) {
                const { children, ...rest } = listboxProps;
                return (
                  <ul {...rest} ref={ref}>
                    <li
                      style={{
                        display: 'flex', alignItems: 'center', padding: '4px 16px',
                        cursor: 'pointer', borderBottom: '1px solid #eee', fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (allSelected) {
                          handleChange(key, []);
                        } else {
                          handleChange(key, [...options]);
                        }
                      }}
                    >
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={allSelected}
                        indeterminate={selected.length > 0 && !allSelected}
                      />
                      {allSelected ? 'Unselect All' : 'Select All'}
                    </li>
                    {children}
                  </ul>
                );
              })}
              renderInput={(params) => (
                <TextField
                   size="small" 
                  {...params}
                  variant="outlined"
                  label={label}
                  placeholder="Search / select"
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    sx: {
                      ...(params.InputLabelProps && params.InputLabelProps.sx ? params.InputLabelProps.sx : {}),
                      fontSize: compact ? '0.78rem' : '0.875rem',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      opacity: 1,
                      fontSize: compact ? '0.74rem' : '0.82rem',
                    },
                    '& .MuiInputBase-root': {
                      fontSize: compact ? '0.78rem' : '0.875rem',
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={option}
                    sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                  />
                ))
              }
            />
          </Grid>
          );
        })}
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Autocomplete
            multiple
            limitTags={1}
            size="small"
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                minHeight: 34,
                maxHeight: 34,
                overflow: 'hidden',
                flexWrap: 'nowrap',
                paddingTop: 0,
                paddingBottom: 0,
              },
              '& .MuiAutocomplete-input': {
                fontSize: compact ? '0.78rem' : '0.875rem',
                minWidth: 0,
              },
              '& .MuiAutocomplete-tag': {
                maxWidth: '100%',
                margin: '1px 2px 1px 0',
              },
              '& .MuiChip-root': {
                maxWidth: '100%',
              },
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& .MuiAutocomplete-endAdornment': {
                top: '50%',
                transform: 'translateY(-50%)',
              },
            }}
            disableCloseOnSelect
            options={complianceFlagOptions}
            value={selectedComplianceFlags}
            onChange={(e, newValue) => setSelectedComplianceFlags(newValue)}
            getOptionLabel={(opt) => opt}
            renderOption={(props, option, { selected: isSelected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={isSelected}
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                size="small"
                {...params}
                variant="outlined"
                label="Compliance Flag Filter"
                placeholder="Search / select"
                InputLabelProps={{
                  ...params.InputLabelProps,
                  sx: {
                    ...(params.InputLabelProps && params.InputLabelProps.sx ? params.InputLabelProps.sx : {}),
                    fontSize: compact ? '0.78rem' : '0.875rem',
                  },
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    opacity: 1,
                    fontSize: compact ? '0.74rem' : '0.82rem',
                  },
                  '& .MuiInputBase-root': {
                    fontSize: compact ? '0.78rem' : '0.875rem',
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  key={option}
                  sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
              ))
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
}
